// Typed designation endpoint functions. Backed by GET /api/v1/designations/*.
//
// Per CHG-1251 plan — consumed by @generated/screens/designation-list.ts +
// designation-detail.ts queryFn callbacks. Spec:
// specs/tier-04-interface/designation-management.api.yaml.
//
// /designations/{id}/donations + /recurring + /campaigns aren't canonical
// at v4 — wrapped as filter bridges on the existing list endpoints
// (`/donations?designation_id={id}` + `/recurring-donations?designation_id={id}`
// + `/campaigns?designation_id={id}`). Forward-compatible: when canonical
// sub-resource endpoints ship, swap the internal URLs in this client
// without touching pages or specs.

import { apiClient } from "./client";
import {
  listPageParams,
  normalizeListResponse,
  type NormalizedList,
  type RawListEnvelope,
} from "./list-contract";
import type {
  Designation,
  Donation,
  RecurringDonation,
  Campaign,
} from "./types";

export interface ListDesignationsParams {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  search?: string;
  status?: string;
  designation_type?: string;
  allow_recurring?: boolean;
}

export async function listDesignations(
  params: ListDesignationsParams = {},
): Promise<NormalizedList<Designation>> {
  const r = await apiClient().get<RawListEnvelope<Designation>>("/designations", {
    // CHG-2916 / ITER-905655 — the /designations router
    // (src/api/app/routers/designations.py:list_designations) returns the
    // `{ data, meta }` envelope (DesignationPagedEnvelope) and reads
    // 1-based `page` / `per_page` / `search` / `status` /
    // `designation_type`. It has NO `sort` and NO `allow_recurring`
    // query param — both were dropped no-ops, so they are no longer sent.
    params: {
      ...listPageParams(params),
      status: params.status,
      designation_type: params.designation_type,
    },
  });
  return normalizeListResponse<Designation>(r.data);
}

export async function getDesignation(id: string): Promise<Designation> {
  const r = await apiClient().get<Designation>(`/designations/${id}`);
  return r.data;
}

// Donations tab bridge — uses `/donations?designation_id={id}` until
// `/designations/{id}/donations` is canonical. CHG-2916 — normalize the
// `{ data, pagination }` envelope the /donations router returns.
export async function getDesignationDonations(
  id: string,
): Promise<NormalizedList<Donation>> {
  const r = await apiClient().get<RawListEnvelope<Donation>>("/donations", {
    params: { designation_id: id },
  });
  return normalizeListResponse<Donation>(r.data);
}

// Recurring tab bridge — `/recurring-donations?designation_id={id}`.
// NOTE (CHG-2916): the /recurring-donations router does NOT read a
// `designation_id` filter param today (it only backs page/per_page/
// search/recurring_status) so this returns the org-wide recurring page
// until the BE adds the filter; envelope normalized regardless.
export async function getDesignationRecurring(
  id: string,
): Promise<NormalizedList<RecurringDonation>> {
  const r = await apiClient().get<RawListEnvelope<RecurringDonation>>(
    "/recurring-donations",
    { params: { designation_id: id } },
  );
  return normalizeListResponse<RecurringDonation>(r.data);
}

// Campaigns tab bridge — `/campaigns?designation_id={id}`.
export async function getDesignationCampaigns(
  id: string,
): Promise<NormalizedList<Campaign>> {
  const r = await apiClient().get<RawListEnvelope<Campaign>>("/campaigns", {
    params: { designation_id: id },
  });
  return normalizeListResponse<Campaign>(r.data);
}

export const designationsApi = {
  listDesignations,
  getDesignation,
  getDesignationDonations,
  getDesignationRecurring,
  getDesignationCampaigns,
};
